import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { OralExamMode } from '@/common/api/models/oralExamMode';

import { isOneToOneExamSurface, oralExamAskedQuestionCount } from './runConduct';

export const PEER_GONE_CLOSING_SECONDS = 30;

/** True when this exam session uses the 1:1 peer-gone banner (not cohort / 1:N). */
export function isOneToOnePeerGoneSurface(
  run: Pick<OralExamRunStateResponse, 'mode' | 'capacity'> | null | undefined,
): boolean {
  return isOneToOneExamSurface(run) && run?.capacity === 1;
}

/** Last asked question — same rule as backend `is_at_last_question`. */
export function isAtLastOralExamQuestion(
  runState: {
    currentQuestionIndex?: number | null;
    selectedQuestionIds?: string[];
    /** Students get this on the wire; generated OralExamRunStateResponse may lag. */
    questionCount?: number;
  } | null | undefined,
): boolean {
  const index = runState?.currentQuestionIndex;
  if (typeof index !== 'number') return false;
  const count = oralExamAskedQuestionCount(runState);
  if (count <= 0) return false;
  return index === count - 1;
}

/** Cohort / 1:N must never show the 1:1 human peer-gone closing banner. */
export function shouldShowOneToOnePeerGoneBanner(
  run: Pick<OralExamRunStateResponse, 'mode' | 'capacity' | 'state'> | null | undefined,
): boolean {
  if (!run || run.state !== 'in_progress') return false;
  if (run.mode === OralExamMode.one_to_n) return false;
  return isOneToOnePeerGoneSurface(run);
}

/** True when the remote peer is gone from LiveKit and/or poll carries lecturer absence. */
export function isOneToOnePeerGone(
  runState: Pick<OralExamRunStateResponse, 'lecturerAbsentAt'> | null | undefined,
  remotePeerPresent: boolean,
): boolean {
  const lecturerAbsentFromPoll = !!runState?.lecturerAbsentAt;
  return !remotePeerPresent || lecturerAbsentFromPoll;
}
